'use client';

import { ReactNode } from 'react';

type ButtonShape = 'circle' | 'rectangle';

export type CircleButtonVariant = 'default' | 'primary' | 'white' | 'outline' | 'gradient';
export type RectangleButtonVariant =
  | 'solidBlack'
  | 'solidPrimary'
  | 'solidWhite'
  | 'solidGray'
  | 'solidGradient'
  | 'solidDisabled'
  | 'outlinePrimary'
  | 'outlineDefault'
  | 'outlineWhite'
  | 'underlinePrimary'
  | 'underlineDefault';

interface PcButtonProps {
  children: ReactNode;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  shape?: ButtonShape;
  variant?: CircleButtonVariant | RectangleButtonVariant;
  size?: number; // circle용
  width?: number; // rectangle용
  height?: number; // rectangle용
  type?: 'button' | 'submit' | 'reset';
  className?: string;
  disabled?: boolean;
}

const circleButtonStyles: Record<CircleButtonVariant, string> = {
  default: 'font-medium bg-secondary-black text-white rounded-full',
  primary: 'font-medium bg-primary-600 text-white rounded-full',
  white: 'font-medium bg-white text-secondary-black rounded-full',
  outline: 'font-medium rounded-full border border-secondary-black',
  gradient: 'font-medium rounded-full bg-gradient-to-br from-[#009EFF] to-[#004EFF] text-white',
};

const rectangleButtonStyles: Record<RectangleButtonVariant, string> = {
  solidBlack: 'font-medium bg-secondary-black py-[8px] px-[16px] text-white rounded-[20px]',
  solidPrimary: 'font-medium bg-primary-600 py-[8px] px-[16px] text-white rounded-[20px]',
  solidWhite:
    'font-medium bg-white py-[8px] px-[16px] text-secondary-black rounded-[20px] gap-[4px]',
  solidGray: 'font-medium bg-[#AAAAAA] py-[8px] px-[16px] text-[#FFFFFF] rounded-[20px]',
  solidDisabled: 'font-medium bg-[#EAEDEF] py-[8px] px-[16px] text-[#FFFFFF] rounded-[20px]',
  solidGradient:
    'font-medium bg-gradient-to-br from-[#009EFF] to-[#004EFF] text-white py-[8px] px-[16px] rounded-[20px]',
  outlinePrimary: 'border border-primary-600 text-primary-600 py-[4px] px-[14px] rounded-[18px]',
  outlineDefault:
    'border border-secondary-black text-secondary-black py-[4px] px-[16px] rounded-[18px]',
  outlineWhite: 'border border-third-grey text-[#101010] py-[4px] px-[14px] rounded-[8px]',
  underlinePrimary: 'border-b-[3px] border-primary-600 text-primary-600 py-[24px]',
  underlineDefault: 'border-b-[3px] border-transparent text-secondary-black py-[24px]',
};

const PcButton = ({
  children,
  onClick,
  shape = 'rectangle',
  variant,
  size = 56,
  width,
  height,
  type = 'button',
  className = '',
  disabled = false,
}: PcButtonProps) => {
  let styleClasses = '';
  let inlineStyle: React.CSSProperties = {};

  if (shape === 'circle') {
    // circle 버튼은 size 사용, 기본 variant는 'default'
    const circleVariant = (variant as CircleButtonVariant) || 'default';
    styleClasses = circleButtonStyles[circleVariant];
    inlineStyle = { width: `${size}px`, height: `${size}px` };
  } else {
    // rectangle 버튼은 width, height 사용, 기본 variant는 'solid'
    const rectVariant = (variant as RectangleButtonVariant) || 'solidBlack';
    styleClasses = rectangleButtonStyles[rectVariant];
    inlineStyle = {
      width: width ? `${width}px` : undefined,
      height: height ? `${height}px` : undefined,
    };
  }

  return (
    <button
      type={type}
      onClick={onClick}
      style={inlineStyle}
      disabled={disabled}
      className={`${styleClasses} ${className} text-locale-14 flex items-center justify-center cursor-pointer disabled:bg-[#EAEDEF] disabled:text-white`}
    >
      {children}
    </button>
  );
};

export default PcButton;
